import React, { useCallback, useState, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Salta capital as default center
const DEFAULT_CENTER = { lat: -24.7821, lng: -65.4232 };

const containerStyle = {
  width: '100%',
  height: '100%',
};

const lightMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d6e0' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

/**
 * Reusable Google Map component.
 *
 * Props:
 *  - markers: Array<{ id, lat, lng, title, ...event }>  (event metadata used in InfoWindow if showEventInfo)
 *  - center: { lat, lng } – optional override
 *  - zoom: number – default 12
 *  - height: CSS height (default 400px)
 *  - showEventInfo: render info window with event details
 */
const GoogleMapView = ({
  markers = [],
  center,
  zoom = 12,
  height = '400px',
  showEventInfo = false,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
  });

  const [activeMarkerId, setActiveMarkerId] = useState(null);

  const mapCenter = useMemo(() => {
    if (center) return center;
    if (markers.length === 1) return { lat: markers[0].lat, lng: markers[0].lng };
    return DEFAULT_CENTER;
  }, [center, markers]);

  const onMapLoad = useCallback(
    (map) => {
      if (markers.length > 1 && window.google) {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
        map.fitBounds(bounds, 60);
      }
    },
    [markers]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div
        className="flex items-center justify-center bg-[#F5F5F5] rounded-lg border border-[#E5E5E5] text-[#737373] text-sm p-6"
        style={{ height }}
        data-testid="map-missing-key"
      >
        Google Maps API key no configurada.
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="flex items-center justify-center bg-[#F5F5F5] rounded-lg border border-[#E5E5E5] text-red-500 text-sm p-6"
        style={{ height }}
        data-testid="map-load-error"
      >
        No se pudo cargar el mapa.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center bg-[#F5F5F5] rounded-lg border border-[#E5E5E5] text-[#737373] text-sm animate-pulse"
        style={{ height }}
        data-testid="map-loading"
      >
        Cargando mapa...
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-[#E5E5E5]" style={{ height }} data-testid="google-map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={markers.length > 1 ? 11 : zoom}
        onLoad={onMapLoad}
        options={{
          styles: lightMapStyles,
          disableDefaultUI: false,
          fullscreenControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          zoomControl: true,
        }}
      >
        {markers.map((m) => (
          <MarkerF
            key={m.id}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.title}
            onClick={() => showEventInfo && setActiveMarkerId(m.id)}
          >
            {showEventInfo && activeMarkerId === m.id && (
              <InfoWindowF onCloseClick={() => setActiveMarkerId(null)}>
                <div className="p-1 max-w-[220px]" data-testid={`map-info-${m.id}`}>
                  <h4 className="font-semibold text-[#0A0A0A] text-sm mb-1 line-clamp-2">{m.title}</h4>
                  {m.start_date && (
                    <div className="flex items-center gap-1 text-xs text-[#525252] mb-1">
                      <Calendar className="w-3 h-3" />
                      <span>{format(new Date(m.start_date), "d MMM, HH:mm", { locale: es })} hs</span>
                    </div>
                  )}
                  {m.address && (
                    <div className="flex items-start gap-1 text-xs text-[#525252] mb-2">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{m.address}</span>
                    </div>
                  )}
                  {m.event_id && (
                    <Link
                      to={`/eventos/${m.event_id}`}
                      className="inline-block text-xs font-semibold text-primary hover:underline"
                      data-testid={`map-info-link-${m.id}`}
                    >
                      Ver detalle →
                    </Link>
                  )}
                </div>
              </InfoWindowF>
            )}
          </MarkerF>
        ))}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapView;
